/**
 * PtyStreamServer — the in-pod half of the direct PTY transport.
 *
 * A plain TCP server speaking the NDJSON frame protocol from
 * `@project/shared` (`pty-stream.ts`). The browser never talks to it directly:
 * it opens a WebSocket to the preview-router's `/workspace-tunnel`, which
 * verifies an HMAC workspace-attach token through the API and only then bridges
 * raw bytes to this port. That is why there is no in-band auth here — exactly
 * like sshd on 2222, the edge is the gate, and the port is denied on the public
 * preview allow-list (`PREVIEW_PORT_DENY_LIST`) so it can never be reached
 * without a per-viewer token.
 *
 * Responsibilities:
 *   - bind the first free port in the shared stream window and report it,
 *   - keep a bounded scrollback ring so a newly-attached viewer sees the
 *     current screen instead of waiting for the next redraw,
 *   - fan output out to every attached viewer,
 *   - fan input in, and reconcile viewers' desired dims to the min box that
 *     fits everyone (the same rule the server-side relay uses).
 */

import net from "node:net";
import {
  PTY_STREAM_PORT_ATTEMPTS,
  PTY_STREAM_PORT_BASE,
  PtyStreamFrameReader,
  encodePtyStreamFrame,
  type PtyStreamFrame,
} from "@project/shared";
import { createServiceLogger } from "../../utils/logger.js";

const logger = createServiceLogger("PtyStreamServer");

/** Mirrors the server-side relay ring (`pty-methods.ts` RING_MAX_CHARS): enough
 *  to replay a full TUI screen plus recent history, bounded so a long-running
 *  session can't grow the agent's heap. */
const RING_MAX_CHARS = 256 * 1024;

interface RingChunk {
  seq: number;
  data: string;
}

export interface PtyStreamServerOptions {
  sessionId: string;
  /** Keystrokes/paste bytes from any attached viewer. */
  onInput: (data: string) => void;
  /** Reconciled (min across viewers) dims. Only fired when the min changes. */
  onResize: (cols: number, rows: number) => void;
  /** Injectable for tests — defaults to the shared stream window. */
  portBase?: number;
  portAttempts?: number;
}

interface StreamClient {
  socket: net.Socket;
  dims: { cols: number; rows: number } | null;
}

export class PtyStreamServer {
  private readonly server: net.Server;
  private readonly clients = new Set<StreamClient>();
  private readonly chunks: RingChunk[] = [];
  private totalChars = 0;
  private lastSeq = 0;
  private dims: { cols: number; rows: number } | null = null;
  private lastReconciled: { cols: number; rows: number } | null = null;
  private boundPort: number | null = null;
  private closed = false;

  constructor(private readonly options: PtyStreamServerOptions) {
    this.server = net.createServer((socket) => this.handleConnection(socket));
    this.server.on("error", (err) => {
      logger.warn(`PTY stream server error: ${err.message}`);
    });
  }

  /** The port the server bound to, or null while unbound. */
  get port(): number | null {
    return this.boundPort;
  }

  /** How many viewers are attached right now. Drives relay coalescing. */
  get clientCount(): number {
    return this.clients.size;
  }

  /**
   * Bind the first free port in the window. Resolves to the bound port, or null
   * when every candidate is taken (a pod running more concurrent agent
   * processes than the window allows) — the caller then simply stays on the
   * relay transport.
   *
   * Binds on all interfaces because the router connects from the cluster
   * network by pod IP, not loopback.
   */
  async listen(): Promise<number | null> {
    const base = this.options.portBase ?? PTY_STREAM_PORT_BASE;
    const attempts = this.options.portAttempts ?? PTY_STREAM_PORT_ATTEMPTS;
    for (let i = 0; i < attempts; i += 1) {
      const port = base + i;
      const ok = await this.tryListen(port);
      if (ok) {
        this.boundPort = port;
        return port;
      }
    }
    logger.warn(`PTY stream server could not bind any port in ${base}..${base + attempts - 1}`);
    return null;
  }

  private tryListen(port: number): Promise<boolean> {
    return new Promise((resolve) => {
      const onError = (): void => {
        this.server.removeListener("listening", onListening);
        resolve(false);
      };
      const onListening = (): void => {
        this.server.removeListener("error", onError);
        resolve(true);
      };
      this.server.once("error", onError);
      this.server.once("listening", onListening);
      this.server.listen(port, "0.0.0.0");
    });
  }

  /** Append a frame to the ring and push it to every attached viewer. */
  broadcast(data: string, dims?: { cols: number; rows: number }): void {
    if (this.closed || data === "") return;
    if (dims) this.dims = dims;
    const seq = ++this.lastSeq;
    this.chunks.push({ seq, data });
    this.totalChars += data.length;
    // Always keep at least one chunk so a single oversized frame still replays.
    while (this.totalChars > RING_MAX_CHARS && this.chunks.length > 1) {
      const evicted = this.chunks.shift();
      if (evicted) this.totalChars -= evicted.data.length;
    }
    if (this.clients.size === 0) return;
    this.send({ t: "data", seq, data, ...(this.dims ?? {}) });
  }

  /** Tell every viewer the PTY died, then drop them. */
  end(): void {
    if (this.clients.size > 0) this.send({ t: "ended" });
    for (const client of [...this.clients]) client.socket.end();
  }

  /** Stop listening and drop every viewer. Idempotent. */
  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.end();
    for (const client of [...this.clients]) client.socket.destroy();
    this.clients.clear();
    await new Promise<void>((resolve) => {
      this.server.close(() => resolve());
    });
    this.boundPort = null;
  }

  private send(frame: PtyStreamFrame): void {
    const line = encodePtyStreamFrame(frame);
    for (const client of this.clients) {
      // Write errors are the socket's problem — 'error'/'close' handle teardown.
      try {
        client.socket.write(line);
      } catch {
        /* dropped viewer; cleanup runs on close */
      }
    }
  }

  private handleConnection(socket: net.Socket): void {
    if (this.closed) {
      socket.destroy();
      return;
    }
    // Terminal traffic is latency-sensitive and tiny; Nagle would stall a
    // keystroke echo behind the 40ms coalescer window.
    socket.setNoDelay(true);
    const client: StreamClient = { socket, dims: null };
    this.clients.add(client);

    const reader = new PtyStreamFrameReader((frame) => this.handleClientFrame(client, frame));
    socket.on("data", (chunk) => reader.push(chunk.toString("utf8")));
    const drop = (): void => {
      if (!this.clients.delete(client)) return;
      // A departing viewer's dims must stop pinning the min box, so the
      // remaining viewers reflow up to their own size.
      this.reconcileDims();
    };
    socket.on("close", drop);
    socket.on("error", drop);

    // Hello + full ring replay, so the viewer paints the current screen
    // immediately rather than after the TUI's next redraw.
    socket.write(
      encodePtyStreamFrame({
        t: "hello",
        sessionId: this.options.sessionId,
        ...(this.dims ?? {}),
      }),
    );
    for (const chunk of this.chunks) {
      socket.write(
        encodePtyStreamFrame({ t: "data", seq: chunk.seq, data: chunk.data, ...(this.dims ?? {}) }),
      );
    }
  }

  private handleClientFrame(client: StreamClient, frame: PtyStreamFrame): void {
    if (frame.t === "input") {
      if (typeof frame.data === "string" && frame.data !== "") this.options.onInput(frame.data);
      return;
    }
    if (frame.t !== "resize") return;
    const { cols, rows } = frame;
    if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols <= 0 || rows <= 0) return;
    client.dims = { cols, rows };
    this.reconcileDims();
  }

  /**
   * A PTY has one size, so multiple viewers get the smallest box that fits
   * everyone — the same min-cols × min-rows rule the server-side relay applies
   * across its attached sockets. Only fires the callback on an actual change so
   * a steady stream of identical proposals doesn't churn the pty.
   */
  private reconcileDims(): void {
    let minCols = Infinity;
    let minRows = Infinity;
    for (const client of this.clients) {
      if (!client.dims) continue;
      if (client.dims.cols < minCols) minCols = client.dims.cols;
      if (client.dims.rows < minRows) minRows = client.dims.rows;
    }
    if (minCols === Infinity || minRows === Infinity) return;
    const prev = this.lastReconciled;
    if (prev && prev.cols === minCols && prev.rows === minRows) return;
    this.lastReconciled = { cols: minCols, rows: minRows };
    this.options.onResize(minCols, minRows);
  }
}
